import React from 'react';
import { Image, Rect, Text, Group } from 'react-konva';

export const renderNode = (node, handleSelect, setTextNodes, onMoveEnd) => {
  const padding = node.padding || { left: 10, right: 10, top: 10, bottom: 10 };
  const finishMove = (event, offsetX = 0, offsetY = 0) => {
    const position = {
      x: Math.round(event.target.x() - offsetX),
      y: Math.round(event.target.y() - offsetY),
    };
    const previous = { x: node.x, y: node.y };
    if (position.x === previous.x && position.y === previous.y) return;
    setTextNodes((current) => current.map((item) => item.id === node.id ? { ...item, ...position } : item));
    onMoveEnd?.(node.id, previous, position);
  };

  switch (node.type) {
    case 'image':
      return (
        <Image
          key={node.id}
          id={`text-${node.id}`}
          image={node.image}
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
          draggable
          onDragEnd={finishMove}
          onClick={(e) => {
            e.cancelBubble = true;
            handleSelect(node.id);
          }}
        />
      );

    case 'eraser': // A removable white cover; the original image stays intact.
      return (
        <Rect
          key={node.id}
          id={`text-${node.id}`}
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
          fill="white"
          rotation={node.rotation || 0}
          draggable
          onDragEnd={finishMove}
          onClick={(e) => {
            e.cancelBubble = true;
            handleSelect(node.id);
          }}
          onTap={(e) => { e.cancelBubble = true; handleSelect(node.id); }}
          onTransformEnd={(e) => {
            const shape = e.target;
            const updatedNode = {
              ...node,
              x: shape.x(),
              y: shape.y(),
              width: Math.max(10, Math.abs(shape.width() * shape.scaleX())),
              height: Math.max(10, Math.abs(shape.height() * shape.scaleY())),
              rotation: shape.rotation(),
            };
            shape.scaleX(1);
            shape.scaleY(1);
            setTextNodes((prev) =>
              prev.map((n) => (n.id === node.id ? updatedNode : n)),
            );
            onMoveEnd?.(node.id, { x: node.x, y: node.y, width: node.width, height: node.height, rotation: node.rotation || 0 },
              { x: updatedNode.x, y: updatedNode.y, width: updatedNode.width, height: updatedNode.height, rotation: updatedNode.rotation });
          }}
        />
      );

    case 'grouped-centered-text': {
      const backgroundMode = node.backgroundMode || 'text';
      return (
        <Group
          key={node.id}
          id={`text-${node.id}`}
          x={node.x}
          y={node.y}
          draggable
          onDragEnd={finishMove}
          onClick={(e) => {
            e.cancelBubble = true;
            handleSelect(node.id);
          }}
        >
          {backgroundMode === 'text' && (
            <Rect width={node.width} height={node.height} fill="white" />
          )}
          <Text
            text={node.text}
            x={padding.left}
            y={padding.top}
            width={node.width - padding.left - padding.right}
            fontSize={node.fontSize}
            fontFamily={node.fontFamily}
            fontStyle={node.fontStyle}
            lineHeight={node.lineHeight}
            align={node.align}
            wrap="word"
            fill="black"
          />
        </Group>
      );
    }

    case 'separator':
      return (
        <Rect
          key={node.id}
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
          fill={node.fill || 'black'}
          listening={false}
        />
      );

    case 'background':
      return (
        <Rect
          key={node.id}
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
          fill="white"
          listening={false}
        />
      );

    case 'text-with-background':
      return (
        <React.Fragment key={node.id}>
          <Rect
            x={node.x}
            y={node.y}
            width={node.width + node.padding.left + node.padding.right}
            height={node.height}
            fill={node.backgroundColor || 'white'} // use white if you prefer
            draggable
            onDragEnd={finishMove}
            onClick={(e) => {
              e.cancelBubble = true;
              handleSelect(node.id);
            }}
          />
          <Text
            id={`text-${node.id}`}
            text={node.text}
            x={node.x + node.padding.left}
            y={node.y + node.padding.top}
            width={node.width}
            fontSize={node.fontSize}
            fontFamily={node.fontFamily}
            fontStyle={node.fontStyle}
            lineHeight={node.lineHeight}
            align={node.align}
            wrap="word"
            fill="black"
            draggable
            onDragEnd={(event) => finishMove(event, padding.left, padding.top)}
            onClick={(e) => {
              e.cancelBubble = true;
              handleSelect(node.id);
            }}
          />
        </React.Fragment>
      );
    case 'text':
    default:
      return (
        <React.Fragment key={node.id}>
          {(node.showBackground !== false) && (
            <Rect
              x={node.x - padding.left}
              y={node.y - padding.top}
              width={node.width + padding.left + padding.right}
              height={node.height}
              fill="white"
              draggable
              onDragEnd={(event) => finishMove(event, -padding.left, -padding.top)}
              onClick={(e) => {
                e.cancelBubble = true;
                handleSelect(node.id);
              }}
            />
          )}
          <Text
            id={`text-${node.id}`}
            text={node.text}
            x={node.x}
            y={node.y}
            width={node.width}
            fontSize={node.fontSize}
            fontFamily={node.fontFamily}
            fontStyle={node.fontStyle}
            lineHeight={node.lineHeight}
            align={node.align || 'left'}
            wrap="word"
            fill="black"
            draggable
            onDragEnd={finishMove}
            onClick={(e) => {
              e.cancelBubble = true;
              handleSelect(node.id);
            }}
          />
        </React.Fragment>
      );
  }
};
